import React from 'react';
import { FastField as FormikField } from 'formik';
import { FormRow, FormColumn } from '@xo-union/tk-component-form-grid';
import { Field as UnionField } from '../../gs-component-form';
import { DisplayButton } from '@xo-union/tk-ui-links';
import { FocusOn } from '../interfaces';
import Dropdown, { DropdownType } from './Dropdown';
import FormattedPhoneNumber from './FormattedPhoneNumber';
import Location from './Location';
import styles from './styles.scss';

interface ContactFieldsProps {
  toggleExpanded: () => void;
  detailsExpanded?: boolean;
  leaderIndex: number;
  focusOn: FocusOn;
}

export const emailPhoneFields = (leaderIndex: number, focusOn: FocusOn, firstInstance = true): JSX.Element => {
  // use this logic to determine if the email or phone field should be shown based on focus state
  // on second instance show whichever wasn't already shown above
  // when focus on is all just show in phone then email order
  if (
    (focusOn === FocusOn.email && firstInstance) ||
    (focusOn === FocusOn.phone && !firstInstance) ||
    (focusOn === FocusOn.all && !firstInstance)
  ) {
    return (
      <FormikField
        name={`people.${leaderIndex}.email`}
        data-testid="email"
        label="Email"
        component={UnionField}
        type="email"
      />
    );
  }

  return (
    <FormikField
      name={`people.${leaderIndex}.phone`}
      data-testid="phone"
      label="Phone"
      component={FormattedPhoneNumber}
      type="tel"
    />
  );
};

const addressFields = (detailsExpanded: boolean | undefined, focusOn: FocusOn, leaderIndex: number) => {
  if (!detailsExpanded && focusOn !== FocusOn.all) return null;
  return (
    <>
      <FormRow>
        <FormColumn md>
          <FormikField
            data-testid="address.street1"
            name="address.street1"
            label="Street Address"
            component={Location}
          />
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormColumn md>
          <FormikField
            data-testid="address.street2"
            name="address.street2"
            label="Apt/Suite/Other"
            component={UnionField}
          />
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormColumn xs="12" md>
          <FormikField data-testid="address.city" name="address.city" label="City" component={UnionField} />
        </FormColumn>
        <FormColumn xs="12" md="3" className={styles.pushForwardZIndex}>
          <Dropdown type={DropdownType.state} />
        </FormColumn>
        <FormColumn xs="12" md="3">
          <FormikField data-testid="address.zip" name="address.zip" label="Zip Code" component={UnionField} />
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormColumn xs="12" md>
          <div className={styles.pushForwardZIndex}>
            <Dropdown type={DropdownType.country} />
          </div>
        </FormColumn>
      </FormRow>
      {focusOn === FocusOn.all && (
        <FormRow>
          <FormColumn md>{emailPhoneFields(leaderIndex, focusOn)}</FormColumn>
          <FormColumn md>{emailPhoneFields(leaderIndex, focusOn, false)}</FormColumn>
        </FormRow>
      )}
    </>
  );
};

const ContactFields: React.FC<ContactFieldsProps> = ({ toggleExpanded, detailsExpanded, leaderIndex, focusOn }) => {
  return (
    <>
      {focusOn !== FocusOn.all && (
        <>
          <FormRow>
            <FormColumn md>{emailPhoneFields(leaderIndex, focusOn)}</FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn md>
              <DisplayButton iconName={detailsExpanded ? 'arrow_up' : 'arrow_down'} onClick={toggleExpanded}>
                {detailsExpanded ? 'Less details' : 'More details'}
              </DisplayButton>
            </FormColumn>
          </FormRow>
          {detailsExpanded && (
            <FormRow>
              <FormColumn md>{emailPhoneFields(leaderIndex, focusOn, false)}</FormColumn>
            </FormRow>
          )}
        </>
      )}
      {addressFields(detailsExpanded, focusOn, leaderIndex)}
    </>
  );
};

export default ContactFields;
